// Segment + measure config for the Replenishment view, over the
// `master_replenishment_summary` cube. Every measure is parked at
// (downstream_node, warehouse, channel, item_key, week) and so is valid at every
// segmentation, which is why this file carries no validity matrix.

/** The module this page reports on. Modules are keyed by a per-client numeric
 *  id, so the stable code crosses the wire and the gateway resolves it. */
export const MODULE_CODE = "Replenishment";

export type Units = "eaches" | "sticks";

// "sticks" is the cube's column suffix, kept as the value; the label names both,
// because the base unit is a stick for one line and a can for the other.
export const UNIT_OPTIONS: { value: Units; label: string }[] = [
  // An each is one saleable unit of the SKU — a 30-count box, a 4-pack of cans,
  // a single can — so no pack word ("boxes", "cases") is true of all of them.
  { value: "eaches", label: "Eaches" },
  { value: "sticks", label: "Sticks / Cans" },
];

/** The segment base units are always split by: a stick and a can are different
 *  things, so a group holding both lines would sum two units into one number. */
export const BASE_UNIT_SPLIT_SEGMENT = "class";

/** The unit mode that needs that split, named once so the two files agree. */
export const BASE_UNIT_MODE: Units = "sticks";

/** How each unit reads in prose. The caption said "sticks" one line under a
 *  control that had stopped calling a can one. */
export const UNIT_LABEL: Record<Units, string> = Object.fromEntries(
  UNIT_OPTIONS.map((o) => [o.value, o.label.toLowerCase()])
) as Record<Units, string>;

/** Suffix the cube uses for the sticks twin of each quantity measure. */
export const STICKS_SUFFIX = "_sticks";

export interface SegmentOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
  disabledReason?: string;
}

/** What a dimension the run's cube never recorded offers instead of grouping. */
export const UNPROFILED_SEGMENT_REASON = "not recorded on this run";

export const SEGMENT_OPTIONS: SegmentOption[] = [
  {
    value: "three_pl",
    label: "3PL",
    description:
      "The logistics partner being replenished. The blank row is everything not " +
      "behind one yet — stock still standing at a co-man, plus any node the run " +
      "left unnamed.",
  },
  {
    value: "warehouse",
    label: "Warehouse",
    description:
      "The site the engine plans at. One partner holds several, and each is " +
      "covered independently — a partner total can read healthy while a site is short.",
    // Segments on the name; the cube is keyed on warehouse_key. Names verified
    // unique per key — segment on the key if that stops holding.
  },
  {
    value: "channel",
    label: "Channel",
    description: "Sales channel of the demand.",
  },
  {
    // Labelled as the Demand page labels it; the value is the engine's column name.
    value: "class",
    label: "Product class",
    description:
      "The line a product belongs to — Drink Mix and Sparkling today. Each is " +
      "built and kitted by its own partners, so this is the coarsest split that " +
      "still plans as one book.",
  },
  {
    value: "item_key",
    label: "SKU",
    description: "The code the engine plans against.",
  },
  {
    value: "sku_name",
    label: "Product",
    description: "The readable name for that code.",
  },
  {
    value: "flavor",
    label: "Flavor",
    description: "Flavor family.",
  },
  {
    value: "pack_count_dim",
    label: "Pack Count",
    description:
      "Pack size for grouping only — not a unit conversion. Items with no pack " +
      "structure group under Unknown.",
  },
  {
    value: "batch_key",
    label: "Batch",
    description:
      "One production run. Its minimum is met across the whole group rather " +
      "than per SKU, so this is the level a run is justified — or refused — at. " +
      "Named by whatever its segment batches on: pack type and flavor, or the " +
      "SKU itself. Blank where nothing batches the product.",
  },
  {
    value: "batch_segment",
    label: "Batch rule set",
    description:
      "Which set of batching rules a product falls under, and so what counts " +
      "as one production run for it. Blank where no segment reaches it — that " +
      "stock is planned with no minimum at all.",
  },
];

/** The options to offer for a run whose cube carries `profiled`.
 *
 *  A run written before a dimension existed keeps a cube without it, and the run
 *  picker offers every completed run — so grouping on one would put the whole
 *  plan in a single blank row beside an identical total. Greyed rather than
 *  dropped: the dimension is a thing this page has, missing on this run.
 *
 *  An EMPTY set is a failed profile read, not a run without dimensions, and
 *  greying the whole control then is worse than offering it. */
export function segmentOptionsFor(profiled: ReadonlySet<string>): SegmentOption[] {
  if (profiled.size === 0) return SEGMENT_OPTIONS;
  return SEGMENT_OPTIONS.map((option) =>
    profiled.has(option.value)
      ? option
      : { ...option, disabled: true, disabledReason: UNPROFILED_SEGMENT_REASON }
  );
}

export const SEGMENT_LABEL: Record<string, string> = Object.fromEntries(
  SEGMENT_OPTIONS.map((o) => [o.value, o.label])
);

/** Default grouping — the two lines are built and kitted apart, so they read as
 *  separate books; within each, a partner total hides a short site. Class first
 *  also matches what base units force, so the grouping survives a unit flip. */
export const DEFAULT_SEGMENTS = ["class", "warehouse"];

/** A retired segment and what it became. The default is written into the URL on
 *  mount, so every link minted before a rename still names the old column; without
 *  this it filters out to nothing and the page opens on one whole-plan total. */
export const RETIRED_SEGMENTS: Record<string, string> = { downstream_node: "three_pl" };

/** Hiding keys on the 3PL being BLANK, which is the definition of unallocated
 *  rather than a name a run happens to carry — so it also works on a cube
 *  published before the engine started labelling the bucket. */
export const UNALLOCATED_COLUMN = "three_pl";

/** Kept as a word rather than a boolean: it rides in the URL beside the other
 *  view params, and "hide" reads in a shared link where "false" does not. */
export type UnallocatedMode = "show" | "hide";

export const UNALLOCATED_LABEL = "Show unallocated";

/** What the filter control calls "no value". Blanks are a real group on this
 *  page — stock not behind a 3PL yet — so they have to be selectable, and a
 *  dropdown cannot carry a null. The read maps this back to IS NULL, so it must
 *  match apply_ui_filters.BLANK_FILTER_VALUE. */
export const BLANK_FILTER_VALUE = "(blank)";

/** The grouping and the one to fall back to when a run predates the 3PL stamp,
 *  so an older run opens on a breakdown rather than one blank row. */
export const THREE_PL_SEGMENT = "three_pl";
export const FALLBACK_SEGMENT = "warehouse";

/** Opens on the position — the question the tab exists to answer. */
export const DEFAULT_MEASURE = "ending_inventory";

// ── Measures ────────────────────────────────────────────────────────────────

/** The engine's own walk lines in reading order, renamed to snake_case once in
 *  logic_replenishment_walk.WALK_LINES and carried unchanged from there. */
export const QUANTITY_MEASURES = [
  "starting_inventory",
  "open_transfer_arrivals",
  "forecasted_transfer_arrivals",
  "build_plan_arrivals",
  "recommended_arrivals",
  "order_backlog",
  "forecasted_demand",
  "unfulfilled_demand",
  "ending_inventory",
  "change_in_inventory",
  "order_rate",
  // Not a walk line — joined onto the cube from the engine's own ledger — but a
  // quantity in the same unit, so it carries a sticks twin like the rest.
  "pre_constraint_build_qty",
] as const;

/** Ratios, which carry no sticks twin. Cover is computed from the eaches columns
 *  in both unit modes: for one item the unit cancels, but across items of
 *  different pack sizes it does not, so an aggregate reads the eaches answer. */
export const RATIO_MEASURES = ["ending_weeks_of_supply"] as const;

/** Coverage walks the ending inventory forward through the actual forecast, so
 *  it needs the whole demand series at the grouping on screen and is recomputed
 *  after every rollup. Must stay in step with `coverage.with_coverage` in steps. */
export const COVERAGE_INPUTS: Record<string, { inventory: string; demand: string[] }> = {
  ending_weeks_of_supply: {
    inventory: "ending_inventory",
    demand: ["order_backlog", "forecasted_demand"],
  },
};

/** A rate, not a quantity, so it carries no sticks twin. */
export const RATE_MEASURE = "order_rate";

/** How each measure reads in the walk equation, top to bottom:
 *
 *      Starting Inventory
 *    + Open Transfer Arrivals
 *    + Planned Builds
 *    - Forecasted Demand
 *    = Ending Inventory
 *
 *  The engine's own identity, asserted in the walk's tests rather than here. */
export type MeasureRole = "open" | "add" | "subtract" | "close" | "ratio" | "reference";

export const MEASURE_ROLE: Record<string, MeasureRole> = {
  target_weeks_of_supply: "reference",
  starting_inventory: "open",
  open_transfer_arrivals: "add",
  // The term is the parent; its two halves are the breakdown the lock reads.
  forecasted_transfer_arrivals: "add",
  build_plan_arrivals: "reference",
  recommended_arrivals: "reference",
  order_backlog: "subtract",
  forecasted_demand: "subtract",
  // Plain, not red: this is usually demand that is late rather than lost, since
  // the engine carries it and serves it when the next arrival lands.
  unfulfilled_demand: "reference",
  ending_inventory: "close",
  ending_weeks_of_supply: "ratio",
  change_in_inventory: "reference",
  order_rate: "reference",
  // Not a term in the walk: this and Recommended Arrivals are the same supply
  // counted before and after the minimum, so adding both would double it.
  pre_constraint_build_qty: "reference",
};

export const ROLE_PREFIX: Record<MeasureRole, string> = {
  open: "",
  add: "+",
  subtract: "\u2212",
  close: "=",
  ratio: "",
  reference: "",
};

export const WALK_MEASURES: string[] = [
  "starting_inventory",
  "open_transfer_arrivals",
  // The engine's one `future_supply` line, undivided — the split rides the lock.
  "forecasted_transfer_arrivals",
  // `order_backlog` is parked like `order_rate` below: the position it was
  // subtracted from is already net of it, so it came off twice.
  // The engine still books it, and the page shows the engine's own
  // `ending_inventory`, so week one reads short of its rows by that amount.
  "forecasted_demand",
  "ending_inventory",
];

/** Highlighted band directly under the walk — the number the page exists for. */
export const BAND_MEASURES: string[] = ["ending_weeks_of_supply"];

export const REFERENCE_MEASURES: string[] = [
  // Read against Recommended Arrivals: ordering more than this is a minimum
  // rounding the order up, ordering nothing is a run declined as too small.
  // Both roll up as `flow` (no ROLLUP_KIND entry); giving either one a kind
  // without the other breaks that comparison at month and above.
  "pre_constraint_build_qty",
  // Context, not a term in the walk, so the equation above does not close in a
  // week that went short — by exactly this much.
  "unfulfilled_demand",
  // The two halves of Planned Builds are parked like `order_rate` below: the walk
  // carries their sum, and the lock — which reads them off the cube — says how
  // much of it is committed.
  "change_in_inventory",
  // `order_rate` is parked, not deleted: the cube still carries it and its
  // label, role and rollup kind are all still here, so putting it back on the
  // page is adding the one string. It came off because nothing on this page
  // divides by it — cover is a forward run-out, not `inventory / rate` — so it
  // read as the denominator of a number it has nothing to do with.
];

export const REFERENCE_LABEL = "REFERENCE";

export const MEASURE_ORDER: string[] = [...WALK_MEASURES, ...BAND_MEASURES, ...REFERENCE_MEASURES];

// Planner-facing wording; the mapping to the engine's own measure ids lives in
// logic_replenishment_walk.WALK_LINES.
export const MEASURE_LABEL: Record<string, string> = {
  starting_inventory: "Starting Inventory",
  open_transfer_arrivals: "Open Transfer Arrivals",
  build_plan_arrivals: "Build Plan Arrivals",
  recommended_arrivals: "Recommended Arrivals",
  forecasted_transfer_arrivals: "Planned Builds",
  order_backlog: "Order Backlog",
  forecasted_demand: "Forecasted Demand",
  unfulfilled_demand: "Unfulfilled Demand",
  ending_inventory: "Ending Inventory",
  change_in_inventory: "Change in Inventory",
  order_rate: "Order Rate",
  pre_constraint_build_qty: "Needed Before Minimums",
  ending_weeks_of_supply: "Ending Weeks of Supply",
  target_weeks_of_supply: "Target Weeks of Supply",
};

export const MEASURE_DESCRIPTION: Record<string, string> = {
  starting_inventory: "Stock on hand at the start of the period.",
  open_transfer_arrivals:
    "Stock that already exists — on hand at the co-man or in transit — arriving this period.",
  build_plan_arrivals: "Build plan arriving this period. Already committed.",
  recommended_arrivals:
    "Supply Atomic recommends ordering to cover demand the build plan does not reach.",
  forecasted_transfer_arrivals:
    "The committed build plan plus the supply Atomic recommends, in the period it lands. " +
    "A solid lock means the period is fully committed, a faint one partly.",
  order_backlog: "Orders still owed to customers.",
  forecasted_demand: "The sales forecast for this period.",
  unfulfilled_demand:
    "Demand this period could not serve. A warehouse cannot hold negative " +
    "stock, so the position stops at zero and the shortfall is named here " +
    "instead of being sunk into it. Usually late rather than lost — the " +
    "engine carries it and serves it when the next arrival lands.",
  ending_inventory:
    "Starting inventory plus arrivals, less demand — floored at zero, since a " +
    "warehouse cannot hold a negative. Where it would have gone below, the " +
    "shortfall is on Unfulfilled Demand.",
  change_in_inventory: "Ending inventory less starting inventory, both floored.",
  order_rate: "Average weekly demand over this period and the next three.",
  pre_constraint_build_qty:
    "What covering demand needed this period, before any minimum order " +
    "quantity was applied. If more than this arrives, a minimum rounded the " +
    "order up. If nothing arrives while this shows a real number, the run " +
    "was too small to be worth building and was declined rather than " +
    "overstocked.",
  ending_weeks_of_supply:
    "How many weeks the ending inventory covers, against the forecast ahead of it.",
  target_weeks_of_supply:
    "The cover this run planned to, from its own settings. Weeks below it are shaded.",
};

/** The cube key for a measure at the chosen units. Ratios and rates ignore
 *  units — a quotient is unit-invariant, and a rate has no sticks twin. */
export function measureKey(measure: string, units: Units): string {
  if ((RATIO_MEASURES as readonly string[]).includes(measure)) return measure;
  if (measure === RATE_MEASURE) return measure;
  return units === "sticks" ? `${measure}${STICKS_SUFFIX}` : measure;
}

/** Decimals every week-denominated number is stated to — the cover, the target
 *  it is graded against, and the band that colours it. One constant because the
 *  three have to agree: a cover printed to one decimal but BANDED on its raw
 *  value shades an identical "8.0" yellow in one row and green in the next. */
export const COVER_DECIMALS = 1;

/** The value a week-denominated cell actually PRINTS, as a number. Anything
 *  grading such a cell has to judge this rather than the float behind it. */
export function shownWeeks(value: number): number {
  return Number(value.toFixed(COVER_DECIMALS));
}

export type CoverBand = "none" | "stockout" | "short" | "on" | "over";

/** How many x the target reads as overstocked, so the green band does not
 *  swallow a node sitting on years of cover. */
export const OVERSTOCK_MULTIPLE = 3;

/** Which heatmap band a cover falls in. `none` shades nothing — a run that
 *  recorded no target gets no verdict, because grading a plan against a number
 *  this side invented is worse than showing none.
 *
 *  Judged on the PRINTED value, not the raw one. Cover is a forward run-out
 *  whose last term is a division, so a row landing exactly on target comes back
 *  as 7.999999999999999 about as often as 8.0 — one ULP, which is arithmetic
 *  noise rather than a shortfall, and which the reader cannot see because both
 *  print "8.0". Banding the raw value made that invisible difference the whole
 *  verdict: in one published run 712 rows printed "8.0" in yellow beside 1,587
 *  printing "8.0" in green. */
export function coverBand(measure: string, value: number, target: number | null): CoverBand {
  if (!BAND_MEASURES.includes(measure) || !Number.isFinite(value)) return "none";
  const shown = shownWeeks(value);
  if (shown <= 0) return "stockout";
  if (target == null) return "none";
  const goal = shownWeeks(target);
  if (shown < goal) return "short";
  // Rounded like the other two: the raw product carries its own ULP — a target
  // printing 1.1 gives 3 * 1.1 === 3.3000000000000003, so a cover printing
  // "3.3" would read `on`, which is the very defect this function exists to
  // stop. Every comparison here is on the one-decimal scale or none of them is.
  if (shown >= shownWeeks(OVERSTOCK_MULTIPLE * goal)) return "over";
  return "on";
}

/** Coverage gets decimals, quantities are rounded integers. Matched against
 *  RATIO_MEASURES rather than a measure name, which has been renamed before and
 *  silently sent the whole column through the integer branch. */
export function formatMeasureCell(measure: string, value: number): string {
  if (!Number.isFinite(value)) return "—";
  // Weeks, not eaches: the target reads beside the cover it grades, so the two
  // must carry the same decimals or 8.6 renders as 9 against a cover of 8.7.
  if (measure === TARGET_MEASURE) return value.toFixed(COVER_DECIMALS);
  if ((RATIO_MEASURES as readonly string[]).includes(measure)) {
    return value.toFixed(COVER_DECIMALS);
  }
  if (measure === "fill_rate") return `${(value * 100).toFixed(1)}%`;
  // `|| 0` collapses negative zero, which would render as "-0" and read as a
  // shortfall that is not there.
  return (Math.round(value) || 0).toLocaleString("en-US");
}

/** The arrivals term and its two halves, named because the cube reads all three
 *  off one row to decide a cell's lock. */
export const ARRIVALS_MEASURE = "forecasted_transfer_arrivals";
export const BUILD_PLAN_MEASURE = "build_plan_arrivals";
export const RECOMMENDED_MEASURE = "recommended_arrivals";

export type Firmness = "none" | "firm" | "mixed";

/** Half a unit — where `formatMeasureCell` starts rounding to zero, so the lock
 *  cannot contradict the cell it sits in. */
export const FIRMNESS_EPSILON = 0.5;

/** How much of a period's arrivals is committed; `mixed` must not lock, because
 *  the engine is still topping that period up.
 *
 *  The lock is earned against the RESIDUAL, not against the recommended line: the
 *  two halves are projected separately from the term, so a row dropped from the
 *  children alone leaves `recommended` at zero while the term still carries it,
 *  and judging that side would lock supply nobody committed to. */
export function firmness(total: number, firm: number, recommended: number): Firmness {
  if (!Number.isFinite(total) || Math.abs(total) < FIRMNESS_EPSILON) return "none";
  const hasFirm = Number.isFinite(firm) && Math.abs(firm) >= FIRMNESS_EPSILON;
  if (!hasFirm) return "none";
  const residual = total - firm;
  const uncommitted = Math.max(
    Math.abs(residual),
    Number.isFinite(recommended) ? Math.abs(recommended) : 0,
  );
  return uncommitted >= FIRMNESS_EPSILON ? "mixed" : "firm";
}

/** What the lock says on hover, in the measures' own words; a mixed period states
 *  both numbers, since "partly committed" without the split cannot be acted on.
 *  Null where no marker is drawn. */
export function firmnessDetail(
  measure: string,
  total: number,
  firm: number,
  recommended: number,
): string | null {
  const state = firmness(total, firm, recommended);
  if (state === "none") return null;
  if (state === "firm") return "Build plan — committed.";
  return (
    `Build plan ${formatMeasureCell(measure, firm)} — committed. ` +
    `Recommended ${formatMeasureCell(measure, recommended)}.`
  );
}

/** The cell's cover target, as a string like every other dimension. Per row:
 *  targets are set per stocking-target group, so one run carries several. */
export const COVER_TARGET_COL = "cover_target_weeks";

/** The target row, shown directly beneath the cover it is measured against.
 *  Not a cube measure — a target is a level, so summing it over the rows in a
 *  segment would multiply it. It rides as a dimension and renders flat. */
export const TARGET_MEASURE = "target_weeks_of_supply";

// Columns the cube emits that are NOT period MM/DD/YY cells — the skip set the
// period-column detector checks before the MM/DD/YY regex.
export const NON_PERIOD_COLS: ReadonlySet<string> = new Set([
  ...SEGMENT_OPTIONS.map((o) => o.value),
  "measure",
  // the cube's own period column, which the transform pivots away but which the
  // detector must never mistake for a period cell
  "period",
  // the run's cover target, carried as a grouping column and read off the row
  COVER_TARGET_COL,
]);

/** The unit whose total is only honest within ONE product line, and the column
 *  that tells them apart.
 *
 *  A stick is not a can, so a number that adds them is wrong rather than
 *  imprecise — and wrong quietly, since it renders like any other total. Shared
 *  because both cubes offer that unit, and a rule kept in two places is a rule
 *  that drifts. */
export const BASE_UNIT_SPLIT_COLUMN = "class";

/** The two product lines, exactly as gold's `class` column spells them
 *  (masterdata's top-down workbook constants). Every cube filter and the two
 *  class pages' route scopes read these — a literal kept per-site would drift
 *  from the data silently, and a drifted value empties the page without an
 *  error. */
export const SPARKLING_CLASS = "Sparkling";
export const DRINK_MIX_CLASS = "Drink Mix";

/** Whether a set of rows spans more than one product line — in which case a
 *  number summed across them is adding two units together. */
export function spansProductLines(lines: readonly string[]): boolean {
  return new Set(lines).size > 1;
}
